"use client"

import { SWRConfig } from "swr"
import { Anchor } from "../../app/components/anchor"
import { DriverAvatar } from "@/components/drivers/driver-avatar"
import PetTravelPage from "@/components/drivers/pet-travel"
import AboutParagraph from "@/components/drivers/profile-description"
import { RelativeTime } from "@/components/relative-time"

import { getDriverExperience } from "@/lib/drivers/date-utils"
import { SWR_CONFIG, CACHE_KEYS } from "@/lib/drivers"
import type { APIResponse, DriverProfile } from "@/lib/drivers/types"
import { isDisplayedDriverReview } from "@/lib/drivers/reviews"
import { RecentTripName } from "./recent-trip-name"
import { formatDate, formatRelativeDate } from "@/util/date"
import { serviceIcons } from "@/constants/drivers/service-icons"
import DriverAchievements from "./driver-achievements"
import DriverVerifications from "./driver-verifications"
import QuoteCTA from "./quoteCTA"

const host = "https://citizenshipper.com"

export function DriverProfileClient({
  profile,
  ctaUrl = "https://app.citizenshipper.com/shipments/new",
  ctaLabel = "Get Transportation Quotes",
}: {
  profile: DriverProfile
  ctaUrl?: string
  ctaLabel?: string
}) {
  const fallback = {
    [CACHE_KEYS.DRIVER_PROFILE(String(profile.data.id))]: profile,
  }

  const sortedTrips =
    profile?.data?.recentTrips.length > 0
      ? profile.data.recentTrips
          .sort((a, b) => {
            const dateA = a.deliveredAt ? new Date(a.deliveredAt) : new Date(0)
            const dateB = b.deliveredAt ? new Date(b.deliveredAt) : new Date(0)

            return dateB.getTime() - dateA.getTime()
          })
          .slice(0, 10)
      : []

  const equipments = Array.from(
    new Set(
      profile.data.offerings
        .flatMap((item) => item.equipment)
        .map((equipment) => JSON.stringify(equipment)),
    ),
  ).map((str) => JSON.parse(str))

  return (
    <SWRConfig value={{ ...SWR_CONFIG, fallback }}>
      <main className="container mx-auto px-4 lg:px-8 max-w-screen-lg">
        <section className="flex flex-row md:items-center mb-8">
          <div className="mt-0 mr-2 md:mr-6">
            <DriverAvatar
              photoUrl={profile.data.profilePhoto?.small ?? null}
              name={profile.data.displayName}
              id={profile.data.id}
              size={80}
            />
          </div>
          <div>
            <h2 className="text-2xl font-bold">
              {profile.data.displayName || "Unnamed Driver"}
            </h2>
            <div className="flex items-center space-x-2 mt-2">
              <span className="text-sm text-gray-700">
                {profile.data.ratingsCount > 0 && (
                  <>
                    <span className="text-yellow-500">{"★"}</span>{" "}
                    {(profile.data.rating ?? 0).toFixed(1)} •{" "}
                  </>
                )}
                {profile.data.wonShipments || 0} Shipments
              </span>
            </div>
            <span className="block text-sm text-gray-700">
              {profile.data.ratingsCount > 0 &&
                `${profile.data.reviews?.length || 0} reviews • `}
              Since{" "}
              {profile.data.joinedAt
                ? getDriverExperience(profile.data.joinedAt).formattedDate
                : "N/A"}
            </span>
          </div>
        </section>

        <div className="border-b my-6" />

        <section className="grid grid-cols-2 gap-2 md:gap-4 mb-8">
          <DriverVerifications
            data={
              profile.data.verifications as APIResponse["data"]["verifications"]
            }
          />
        </section>

        <div className="border-b my-6" />
        {profile?.data?.about && (
          <AboutParagraph text={profile.data.about || ""} />
        )}

        {profile.data.offerings?.[0]?.services && (
          <section className="mb-8 mt-8" id="services">
            <h2 className="text-2xl font-semibold mb-4">Services</h2>
            <div className="grid grid-cols-2 gap-4">
              {Array.from(
                new Set(
                  profile.data.offerings?.[0]?.services
                    ?.filter(Boolean)
                    .map((service) => service.name),
                ),
              ).map((serviceName) => {
                const service = profile.data.offerings?.[0]?.services?.find(
                  (s) => s.name === serviceName,
                )

                return (
                  <div key={serviceName} className="bg-gray-50 rounded-lg p-3">
                    <p className="flex items-center">
                      <span className="pr-2 text-gray-700">
                        {
                          serviceIcons[
                            service?.name as keyof typeof serviceIcons
                          ]
                        }
                      </span>
                      {serviceName}
                    </p>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {profile.data.offerings?.[0]?.equipment && (
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              Additional Equipment
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {equipments.map((item) => (
                <div key={item.name} className="bg-gray-50 rounded-lg p-3">
                  <p className="flex items-center">
                    {item.name === "Food" && (
                      <svg width="32" height="32" fill="none">
                        <path
                          d="M29 23v.5h.6l-.108-.59L29 23ZM3 23l-.492-.09-.107.59H3V23Zm2.834-10.5h20.33v-1H5.834v1Zm20.822.41 1.85 10.18.986-.18-1.851-10.178-.985.179v-.001ZM29 22.5H3v1h26v-1Zm-25.508.59 1.85-10.18-.983-.178-1.85 10.179.983.179ZM26.164 12.5a.503.503 0 0 1 .492.41l.985-.178a1.5 1.5 0 0 0-1.477-1.232v1Zm-20.33-1a1.5 1.5 0 0 0-1.475 1.232l.984.179a.5.5 0 0 1 .49-.411l.002-1Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M29 23H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h26a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1Z"
                          stroke="#1A1A1A"
                        />
                        <path
                          d="M11 9v.5a.5.5 0 0 0 .447-.276L11 9Zm3.5-2v.5a.5.5 0 0 0 .354-.146L14.5 7ZM20 8.5h-.5a.5.5 0 0 0 .5.5v-.5ZM8.5 12c0-.42.13-1.061.506-1.583.357-.496.957-.917 1.994-.917v-1c-1.363 0-2.263.58-2.806 1.333A3.85 3.85 0 0 0 7.5 12h1Zm2.947-2.776c.256-.512.46-.905.853-1.199.383-.288 1.013-.525 2.2-.525v-1c-1.313 0-2.183.263-2.8.725-.607.456-.903 1.063-1.147 1.551l.894.448Zm3.407-1.87c.161-.162.367-.366.691-.537.322-.169.783-.317 1.455-.317v-1c-.828 0-1.45.185-1.92.433-.467.245-.762.542-.934.713l.708.708ZM17 6.5c1.724 0 2.5 1.276 2.5 2h1c0-1.276-1.224-3-3.5-3v1ZM20 9c.84 0 1.725.426 2.412 1.056.698.64 1.088 1.392 1.088 1.944h1c0-.948-.61-1.946-1.412-2.681C22.275 8.574 21.16 8 20 8v1Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M10 17a1 1 0 1 0-2 0 1 1 0 0 0 2 0ZM17 17a1 1 0 1 0-2 0 1 1 0 0 0 2 0ZM24 17a1 1 0 1 0-2 0 1 1 0 0 0 2 0Z"
                          stroke="#1A1A1A"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {item.name === "Specialty Food" && (
                      <svg
                        width="32"
                        height="32"
                        fill="#000000"
                        viewBox="0 0 196 256"
                        enable-background="new 0 0 196 256"
                      >
                        <path
                          d="M79.232,158.234h37.723l50.132-51.566H155.34c0.552-1.765,0.882-3.53,0.882-5.46c0-6.783-3.53-12.795-9.376-16.104
	c0.165-0.827,0.165-1.599,0.165-2.427c0-10.258-8.383-18.641-18.641-18.641c-5.901,0-11.361,2.813-14.835,7.39
	c-1.93-1.765-4.247-3.199-6.839-4.026c-0.552-9.762-8.714-17.483-18.531-17.483c-6.287,0-12.023,3.144-15.387,8.217
	c-2.427-1.103-5.019-1.71-7.666-1.71c-10.258,0-18.641,8.383-18.641,18.641c0,3.309,0.882,6.508,2.482,9.376
	c-5.57,3.309-9.21,9.376-9.21,15.938c0,2.151,0.496,4.247,1.103,6.232H28.935L79.232,158.234z M46.583,100.437
	c0-5.294,3.474-9.927,8.603-11.471l5.294-1.544l-3.861-3.971c-2.151-2.261-3.364-5.239-3.364-8.383
	c0-6.618,5.405-12.023,12.023-12.023c2.482,0,4.908,0.827,7.004,2.261l3.364,2.482l1.599-3.861c1.875-4.522,6.232-7.39,11.03-7.39
	c6.618,0,12.023,5.405,12.023,12.023c0,0.165,0,0.331-0.055,0.496l-0.386,3.805l3.144,0.496c3.53,0.552,6.618,2.647,8.438,5.736
	l3.53,6.067l2.427-6.508c1.71-4.688,6.232-7.831,11.196-7.831c6.618,0,12.023,5.405,12.023,12.023c0,0.993-0.165,2.041-0.551,3.309
	l-0.882,2.923l2.813,1.103c4.688,1.765,7.666,6.232,7.666,11.14c0,2.041-0.552,3.861-1.379,5.46H48.459
	C47.3,104.904,46.583,102.808,46.583,100.437z M118.644,215.096c0-7.527,2.901-15.053,8.624-20.855l34.811-34.811
	c3.685-3.685,9.644-3.685,13.328,0c3.685,3.685,3.685,9.644,0,13.328l-28.068,28.068c-1.254,1.098-1.254,3.058,0,4.312
	c1.254,1.254,3.215,1.254,4.469,0l27.911-27.911c3.058-2.901,4.626-6.899,4.626-11.055s-1.725-8.154-4.626-11.055
	c-1.254-1.411-3.058-2.587-4.783-3.371v-39.593c0-5.253,4.155-9.487,9.33-9.487c5.253,0,9.487,4.312,9.487,9.487V176.6
	c0.157,7.683-2.744,15.994-8.624,21.953l-24.226,24.226c-3.058,3.058-4.626,7.056-4.626,11.055V254h-37.555v-38.904H118.644z
	 M77.356,215.096c0-7.527-2.901-15.053-8.624-20.855L33.921,159.43c-3.685-3.685-9.644-3.685-13.328,0
	c-3.685,3.685-3.685,9.644,0,13.328l28.068,28.068c1.254,1.098,1.254,3.058,0,4.312c-1.254,1.254-3.215,1.254-4.469,0
	l-27.911-27.911c-3.058-2.901-4.626-6.899-4.626-11.055s1.725-8.154,4.626-11.055c1.254-1.411,3.058-2.587,4.783-3.371v-39.593
	c0-5.253-4.155-9.487-9.33-9.487c-5.253,0-9.487,4.312-9.487,9.487V176.6c-0.157,7.683,2.744,15.994,8.624,21.953l24.226,24.226
	c3.058,3.058,4.626,7.056,4.626,11.055V254h37.555v-38.904H77.356z M99.398,42.888c-2.849-2.964-4.418-6.864-4.418-10.982
	c0-3.826,1.499-7.592,4.336-10.892c1.511-1.758,3.276-4.916,3.276-8.032c0-2.979-1.134-5.799-3.193-7.941L102.562,2
	c2.849,2.964,4.418,6.864,4.418,10.982c0,4.312-2.24,8.454-4.336,10.892c-2.173,2.529-3.275,5.231-3.275,8.032
	c0,2.979,1.134,5.799,3.193,7.941L99.398,42.888z M118.311,35.167c-1.451-1.51-2.251-3.499-2.251-5.601
	c0-1.975,0.785-3.89,2.334-5.691c1.641-1.909,3.395-5.158,3.395-8.552c0-3.241-1.235-6.311-3.478-8.642l-3.163,3.042
	c1.452,1.51,2.252,3.5,2.252,5.601c0,2.197-1.258,4.439-2.334,5.692c-2.221,2.583-3.394,5.541-3.394,8.552
	c0,3.242,1.235,6.311,3.477,8.642L118.311,35.167z M86.771,35.167c-1.452-1.51-2.252-3.5-2.252-5.601
	c0-1.975,0.785-3.89,2.334-5.691c1.641-1.909,3.394-5.159,3.394-8.552c0-3.241-1.235-6.31-3.476-8.642l-3.164,3.041
	c1.452,1.511,2.252,3.5,2.252,5.601c0,2.196-1.257,4.439-2.334,5.692c-2.22,2.583-3.394,5.541-3.394,8.551
	c0,3.241,1.235,6.311,3.476,8.642L86.771,35.167z"
                        />
                      </svg>
                    )}
                    {item.name === "Water" && (
                      <svg width="32" height="32" fill="none">
                        <path
                          d="M25.5 19a9.5 9.5 0 0 1-9.5 9.5v1c5.799 0 10.5-4.701 10.5-10.5h-1ZM16 28.5A9.5 9.5 0 0 1 6.5 19h-1c0 5.799 4.701 10.5 10.5 10.5v-1ZM6.5 19c0-2.502.858-4.575 2.432-6.947 1.594-2.4 3.857-5.02 6.658-8.684l-.794-.607C12.048 6.354 9.715 9.066 8.099 11.5 6.464 13.964 5.5 16.23 5.5 19h1Zm9.91-15.63c2.802 3.662 5.064 6.282 6.658 8.683C24.642 14.425 25.5 16.498 25.5 19h1c0-2.77-.964-5.036-2.599-7.5-1.616-2.434-3.95-5.146-6.697-8.738l-.794.608Zm-.82-.001a.517.517 0 0 1 .638-.147.52.52 0 0 1 .182.148l.794-.608a1.519 1.519 0 0 0-2.408 0l.794.607Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M22.5 19a.5.5 0 0 0-1 0h1ZM16 24.5a.5.5 0 0 0 0 1v-1Zm5.5-5.5a5.5 5.5 0 0 1-5.5 5.5v1a6.5 6.5 0 0 0 6.5-6.5h-1Z"
                          fill="#1A1A1A"
                        />
                      </svg>
                    )}
                    {item.name === "Leashes" && (
                      <svg width="32" height="32" fill="none">
                        <path
                          d="M20.27 11.217A1 1 0 0 1 21.247 10h5.506a1 1 0 0 1 .977 1.217l-.89 4a.999.999 0 0 1-.976.783h-3.73a1 1 0 0 1-.975-.783l-.89-4Z"
                          stroke="#1A1A1A"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22.5 12h3l-.5 2h-2l-.5-2Z"
                          stroke="#1A1A1A"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M23.5 16v9h1v-9h-1Zm0 9c0 .518-.01 1.129-.304 1.618-.26.433-.818.882-2.196.882v1c1.622 0 2.564-.551 3.054-1.368.456-.76.446-1.65.446-2.132h-1ZM21 27.5c-1.378 0-1.936-.449-2.196-.882-.294-.49-.304-1.1-.304-1.618h-1c0 .482-.01 1.371.446 2.132.49.817 1.432 1.368 3.054 1.368v-1ZM18.5 25v-6h-1v6h1Zm0-6c0-.482.008-1.352-.365-2.099a2.356 2.356 0 0 0-.94-1.006c-.444-.256-1.004-.395-1.695-.395v1c.559 0 .936.111 1.195.261.256.148.427.35.545.588.252.503.26 1.133.26 1.651h1Zm-3-3.5c-.691 0-1.251.139-1.695.395a2.356 2.356 0 0 0-.94 1.006c-.373.747-.365 1.617-.365 2.099h1c0-.518.008-1.148.26-1.651.118-.238.29-.44.545-.588.259-.15.636-.261 1.195-.261v-1Zm-3 3.5v6h1v-6h-1Zm0 6c0 .518-.01 1.129-.304 1.618-.26.433-.818.882-2.196.882v1c1.622 0 2.564-.551 3.054-1.368.456-.76.446-1.65.446-2.132h-1ZM10 27.5c-.559 0-.936-.111-1.195-.261a1.356 1.356 0 0 1-.545-.588C8.008 26.148 8 25.518 8 25H7c0 .482-.008 1.352.365 2.099.194.387.492.747.94 1.006.444.256 1.004.395 1.695.395v-1ZM8 25V13H7v12h1Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M6 11h3v2H6v-2ZM7.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
                          stroke="#1A1A1A"
                        />
                        <path
                          d="M7.5 9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"
                          stroke="#1A1A1A"
                        />
                      </svg>
                    )}
                    {item.name.includes("Collars") && (
                      <svg width="32" height="32" fill="none">
                        <path
                          d="M2 10.5v4c0 2.905 5.735 5.285 13 5.486M30 10.5v4c0 2.905-5.735 5.285-13 5.486"
                          stroke="#1A1A1A"
                        />
                        <path
                          d="M2.954 12.5C4.991 10.451 10.064 9 16 9c5.936 0 11.01 1.451 13.045 3.5M17 16a1 1 0 1 0-2 0v4a1 1 0 1 0 2 0v-4ZM16 29a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                          stroke="#1A1A1A"
                        />
                        <path
                          d="M16 21v2m1-7.014c7.265-.201 13-2.58 13-5.486C30 7.462 23.732 5 16 5S2 7.462 2 10.5c0 2.905 5.735 5.285 13 5.486"
                          stroke="#1A1A1A"
                        />
                      </svg>
                    )}
                    {item.name.includes("Medium Crates") && (
                      <svg width="32" height="32" fill="none">
                        <path
                          d="M11.714 7.429V4.857c0-.473.384-.857.857-.857h6.857c.473 0 .857.384.857.857V7.43"
                          stroke="#1A1A1A"
                          strokeWidth=".857"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.429 7.428V5.714h5.143v1.714"
                          stroke="#1A1A1A"
                          strokeWidth=".857"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4.013 15.896a.429.429 0 0 0 .831.208l-.831-.208Zm23.143.208a.429.429 0 0 0 .831-.208l-.831.208Zm-22.312 0 1.981-7.922-.832-.208-1.98 7.922.831.208Zm23.143-.208-1.98-7.922-.832.208 1.98 7.922.832-.208ZM7.241 7.857h17.518V7H7.241v.857Zm18.766.117A1.286 1.286 0 0 0 24.759 7v.857c.197 0 .369.134.416.325l.832-.208Zm-19.182.208a.429.429 0 0 1 .416-.325V7c-.59 0-1.104.402-1.248.974l.832.208ZM3.584 17.818a.429.429 0 0 1 .832-.208l-.832.208Zm24-.208a.429.429 0 0 1 .832.208l-.832-.208Zm-23.168 0 2.409 9.637-.832.208-2.409-9.637.832-.208Zm24 .208-2.41 9.637-.83-.208 2.408-9.637.832.208ZM7.24 27.571h17.518v.858H7.241v-.858Zm18.766-.116a1.286 1.286 0 0 1-1.248.974v-.858a.429.429 0 0 0 .416-.324l.832.208Zm-19.182-.208c.048.175.201.297.416.324v.858c-.54 0-1.012-.368-1.143-.893l.832-.208Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M13.428 16H4a.857.857 0 0 0 0 1.714h9.428M18.57 16H28a.857.857 0 0 1 0 1.714h-9.43M16 9.571h0c-.71 0-1.286.576-1.286 1.286v1.714c0 .71.575 1.286 1.285 1.286h0c.71 0 1.286-.576 1.286-1.286v-1.714c0-.71-.576-1.286-1.286-1.286ZM20.588 9.644h0a1.286 1.286 0 0 0-.909 1.575l.444 1.656a1.286 1.286 0 0 0 1.574.909h0a1.286 1.286 0 0 0 .91-1.575l-.444-1.656a1.286 1.286 0 0 0-1.575-.909ZM11.411 9.645h0a1.286 1.286 0 0 0-1.575.909l-.443 1.656c-.184.686.223 1.39.909 1.574h0a1.286 1.286 0 0 0 1.575-.909l.443-1.656a1.286 1.286 0 0 0-.909-1.574ZM16 21.07h0c-.71 0-1.286.576-1.286 1.286v1.714c0 .71.575 1.286 1.285 1.286h0c.71 0 1.286-.576 1.286-1.286v-1.714c0-.71-.576-1.286-1.286-1.286ZM20.588 25.283h0a1.286 1.286 0 0 1-.909-1.575l.444-1.656a1.286 1.286 0 0 1 1.574-.91h0c.686.185 1.093.89.91 1.576l-.444 1.655a1.286 1.286 0 0 1-1.575.91ZM11.411 25.283h0a1.286 1.286 0 0 1-1.575-.91l-.443-1.655a1.286 1.286 0 0 1 .909-1.575h0a1.286 1.286 0 0 1 1.575.91l.443 1.655a1.286 1.286 0 0 1-.909 1.575Z"
                          stroke="#1A1A1A"
                          strokeWidth=".857"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.571 15.214h-3.143a.786.786 0 0 0-.785.786v1.571c0 .434.351.786.785.786h3.143a.786.786 0 0 0 .786-.786V16a.786.786 0 0 0-.786-.786Z"
                          stroke="#1A1A1A"
                          strokeWidth=".786"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {item.name === "Blankets" && (
                      <svg width="32" height="32" fill="none">
                        <path
                          d="M24.5 7a.5.5 0 0 0 0-1v1Zm-1 3a.5.5 0 0 0 0 1v-1Zm1-4H9v1h15.5V6ZM9 11h18.25v-1H9v1Zm18.25 3.5H9v1h18.25v-1ZM9 3.5h15.75v-1H9v1ZM24.75 10H23.5v1h1.25v-1ZM28 6.75A3.25 3.25 0 0 1 24.75 10v1A4.25 4.25 0 0 0 29 6.75h-1ZM24.75 3.5A3.25 3.25 0 0 1 28 6.75h1a4.25 4.25 0 0 0-4.25-4.25v1ZM3.5 9A5.5 5.5 0 0 1 9 3.5v-1A6.5 6.5 0 0 0 2.5 9h1ZM9 14.5A5.5 5.5 0 0 1 3.5 9h-1A6.5 6.5 0 0 0 9 15.5v-1Zm20-1.75a1.75 1.75 0 0 1-1.75 1.75v1A2.75 2.75 0 0 0 30 12.75h-1ZM27.25 11c.966 0 1.75.784 1.75 1.75h1A2.75 2.75 0 0 0 27.25 10v1ZM6.5 8.5A2.5 2.5 0 0 0 9 11v-1a1.5 1.5 0 0 1-1.5-1.5h-1ZM9 6a2.5 2.5 0 0 0-2.5 2.5h1A1.5 1.5 0 0 1 9 7V6Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M24.5 19a.5.5 0 0 0 0-1v1Zm-1 3a.5.5 0 0 0 0 1v-1Zm1-4H9v1h15.5v-1ZM9 23h18.25v-1H9v1Zm18.25 3.5H9v1h18.25v-1ZM9 15.5h15.75v-1H9v1ZM24.75 22H23.5v1h1.25v-1ZM28 18.75A3.25 3.25 0 0 1 24.75 22v1A4.25 4.25 0 0 0 29 18.75h-1Zm-3.25-3.25A3.25 3.25 0 0 1 28 18.75h1a4.25 4.25 0 0 0-4.25-4.25v1ZM3.5 21A5.5 5.5 0 0 1 9 15.5v-1A6.5 6.5 0 0 0 2.5 21h1ZM9 26.5A5.5 5.5 0 0 1 3.5 21h-1A6.5 6.5 0 0 0 9 27.5v-1Zm20-1.75a1.75 1.75 0 0 1-1.75 1.75v1A2.75 2.75 0 0 0 30 24.75h-1ZM27.25 23c.966 0 1.75.784 1.75 1.75h1A2.75 2.75 0 0 0 27.25 22v1ZM6.5 20.5A2.5 2.5 0 0 0 9 23v-1a1.5 1.5 0 0 1-1.5-1.5h-1ZM9 18a2.5 2.5 0 0 0-2.5 2.5h1A1.5 1.5 0 0 1 9 19v-1Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M28 27H4a1 1 0 1 0 0 2h24a1 1 0 1 0 0-2Z"
                          stroke="#1A1A1A"
                        />
                      </svg>
                    )}
                    {item.name === "Small Crates" && (
                      <svg width="32" height="32" fill="none">
                        <path
                          d="M12.071 8.143V5.786c0-.434.352-.786.786-.786h6.286c.434 0 .785.352.785.786v2.357"
                          stroke="#1A1A1A"
                          strokeWidth=".786"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.643 8.143V6.57h5.143v1.714"
                          stroke="#1A1A1A"
                          strokeWidth=".786"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.011 15.904a.393.393 0 0 0 .763.191l-.763-.19Zm21.215.191a.393.393 0 0 0 .762-.19l-.762.19Zm-20.452 0 1.815-7.262-.762-.19-1.816 7.261.763.191Zm21.214-.19-1.816-7.263-.762.191 1.816 7.262.762-.19ZM7.241 7.857h17.518V7H7.241v.857Zm18.766.117A1.286 1.286 0 0 0 24.759 7v.857c.197 0 .369.134.416.325l.832-.208Zm-19.182.208a.429.429 0 0 1 .416-.325V7c-.59 0-1.104.402-1.248.974l.832.208ZM3.584 17.818a.429.429 0 0 1 .832-.208l-.832.208Zm24-.208a.429.429 0 0 1 .832.208l-.832-.208Zm-23.168 0 2.409 9.637-.832.208-2.409-9.637.832-.208Zm24 .208-2.41 9.637-.83-.208 2.408-9.637.832.208ZM7.24 27.571h17.518v.858H7.241v-.858Zm18.766-.116a1.286 1.286 0 0 1-1.248.974v-.858a.429.429 0 0 0 .416-.324l.832.208Zm-19.182-.208c.048.175.201.297.416.324v.858c-.54 0-1.012-.368-1.143-.893l.832-.208Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M13.642 16H4a.857.857 0 0 0 0 1.714h9.428M18.357 16H28a.857.857 0 0 1 0 1.714h-9.43M17.178 11.285a1.179 1.179 0 0 0-2.357 0v1.572a1.179 1.179 0 1 0 2.357 0v-1.572ZM21.649 11.007a1.179 1.179 0 0 0-2.277.61l.407 1.518a1.179 1.179 0 0 0 2.277-.61l-.407-1.518a1.179 1.179 0 0 0-2.277-.61l.407 1.518a1.179 1.179 0 0 0 2.277.61ZM12.627 11.617a1.179 1.179 0 1 0-2.276-.61l-.407 1.518a1.179 1.179 0 0 0 2.277.61l.406-1.518a1.179 1.179 0 0 0-2.277-.61l.407 1.518a1.179 1.179 0 0 0 2.277.61ZM17.178 21.826a1.179 1.179 0 0 0-2.357 0v1.572a1.179 1.179 0 1 0 2.357 0v-1.572ZM21.649 23.676a1.179 1.179 0 0 1-2.277-.61l.407-1.518a1.179 1.179 0 0 1 2.277.61l-.407 1.518ZM12.627 23.066a1.179 1.179 0 1 1-2.276.61l-.407-1.518a1.179 1.179 0 0 1 .909-1.575h0a1.179 1.179 0 0 1 1.575.91l.443 1.655a1.179 1.179 0 0 1-.909 1.575Z"
                          stroke="#1A1A1A"
                          strokeWidth=".786"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.571 15.214h-3.143a.786.786 0 0 0-.785.786v1.571c0 .434.351.786.785.786h3.143a.786.786 0 0 0 .786-.786V16a.786.786 0 0 0-.786-.786Z"
                          stroke="#1A1A1A"
                          strokeWidth=".786"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {item.name === "Large Crates" && (
                      <svg width="32" height="32" fill="none">
                        <path
                          d="M11 6V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3"
                          stroke="#1A1A1A"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13 6V4h6v2"
                          stroke="#1A1A1A"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2.015 15.879a.5.5 0 0 0 .97.242l-.97-.242Zm27 .242a.5.5 0 0 0 .97-.242l-.97.242Zm-26.03 0 2.31-9.242-.97-.243-2.31 9.243.97.242Zm27-.242-2.31-9.243-.971.243 2.31 9.242.97-.242ZM5.78 6.5h20.438v-1H5.78v1Zm21.894.136A1.502 1.502 0 0 0 26.219 5.5v1a.503.503 0 0 1 .485.379l.97-.243Zm-22.38.244a.5.5 0 0 1 .486-.38v-1a1.503 1.503 0 0 0-1.456 1.136l.97.243v.001Zm-3.78 11.241a.5.5 0 0 1 .97-.242l-.97.242Zm28-.242a.5.5 0 0 1 .97.242l-.97-.242Zm-27.03 0 2.81 11.243-.97.242-2.81-11.243.97-.242Zm28 .208-2.41 9.637-.83-.208 2.408-9.637.832.208ZM5.78 29.5h20.438v1H5.78v-1Zm21.893-.136a1.5 1.5 0 0 1-1.455 1.136v-1a.503.503 0 0 0 .486-.378l.97.243-.001-.001ZM5.296 29.12a.5.5 0 0 0 .485.38v1a1.5 1.5 0 0 1-1.455-1.137l.97-.243Z"
                          fill="#1A1A1A"
                        />
                        <path
                          d="M13 16H2a1 1 0 0 0 0 2h11m6-2h11a1 1 0 0 1 0 2H19M17.5 10a1.5 1.5 0 0 0-3 0v2a1.5 1.5 0 0 0 3 0v-2ZM23.19 9.646a1.5 1.5 0 0 0-2.898.777l.518 1.932a1.5 1.5 0 0 0 2.898-.777l-.518-1.932ZM11.707 10.422a1.5 1.5 0 1 0-2.897-.776l-.518 1.932a1.5 1.5 0 1 0 2.898.776l.518-1.932a1.5 1.5 0 0 0-2.898-.776l.518 1.932a1.5 1.5 0 0 0 2.898.776ZM16 21.07h0c-.71 0-1.286.576-1.286 1.286v1.714c0 .71.575 1.286 1.285 1.286h0c.71 0 1.286-.576 1.286-1.286v-1.714c0-.71-.576-1.286-1.286-1.286ZM20.588 25.283h0a1.286 1.286 0 0 1-.909-1.575l.444-1.656a1.286 1.286 0 0 1 1.574-.91h0c.686.185 1.093.89.91 1.576l-.444 1.655a1.286 1.286 0 0 1-1.575.91ZM11.411 25.283h0a1.286 1.286 0 0 1-1.575-.91l-.443-1.655a1.286 1.286 0 0 1 .909-1.575h0a1.286 1.286 0 0 1 1.575.91l.443 1.655a1.286 1.286 0 0 1-.909 1.575Z"
                          stroke="#1A1A1A"
                          strokeWidth=".857"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.571 15.214h-3.143a.786.786 0 0 0-.785.786v1.571c0 .434.351.786.785.786h3.143a.786.786 0 0 0 .786-.786V16a.786.786 0 0 0-.786-.786Z"
                          stroke="#1A1A1A"
                          strokeWidth=".786"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    <span className="pl-2">{item.name}</span>
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="border-b my-6" />

        <DriverAchievements
          wonShipments={profile.data.wonShipments}
          rating={profile.data.rating}
          ratingsCount={profile.data.ratingsCount}
        />
        <QuoteCTA ctaUrl={ctaUrl} ctaLabel={ctaLabel} />
        {!!profile.data.recentTrips.length && (
          <section className="mt-8">
            <h2 className="text-2xl font-semibold mb-4">Recent trips</h2>
            <div>
              {sortedTrips.map((trip) => (
                <div
                  key={trip.name}
                  className="p-4 flex-1 border border-gray15 rounded mb-4"
                >
                  <p>
                    <RecentTripName category={trip.category} name={trip.name} />
                  </p>
                  <p className="text-m text-gray-700 flex items-center">
                    <span className="flex items-center">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_4020_51)">
                          <path
                            d="M4.22873 4.22876V5.55812L9.49432 5.56283L3.75732 11.2998L4.70013 12.2426L10.4371 6.50564L10.4418 11.7712H11.7712V4.22876H4.22873Z"
                            fill="#333333"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_4020_51">
                            <rect
                              width="16"
                              height="16"
                              fill="white"
                              transform="matrix(1 0 0 -1 0 16)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                      {trip?.deliveredAt && formatDate(trip?.deliveredAt)}
                    </span>
                    {trip?.deliveredAt && (
                      <span className="px-2 flex justify-center items-center">
                        {" "}
                        <svg
                          width="4"
                          height="4"
                          viewBox="0 0 4 4"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2 0.196C2.812 0.196 3.484 0.868 3.484 1.68C3.484 2.506 2.812 3.178 2 3.178C1.174 3.178 0.502 2.506 0.502 1.68C0.502 0.868 1.174 0.196 2 0.196Z"
                            fill="#4D4D4D"
                          />
                        </svg>
                      </span>
                    )}
                    <span>
                      {" "}
                      Posted{" "}
                      {trip?.postedAt && (
                        <RelativeTime
                          date={trip.postedAt}
                          formatRelative={formatRelativeDate}
                        />
                      )}
                    </span>
                    <span className="px-2 flex justify-center items-center">
                      <span className="pr-2 ">
                        <svg
                          width="4"
                          height="4"
                          viewBox="0 0 4 4"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2 0.196C2.812 0.196 3.484 0.868 3.484 1.68C3.484 2.506 2.812 3.178 2 3.178C1.174 3.178 0.502 2.506 0.502 1.68C0.502 0.868 1.174 0.196 2 0.196Z"
                            fill="#4D4D4D"
                          />
                        </svg>
                      </span>

                      {trip?.category}
                    </span>
                    {false && (
                      <span className="flex items-center px-4 ">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clipPath="url(#clip0_4020_60)">
                            <path
                              d="M10.0001 9.99992C8.15841 9.99992 6.66675 8.50825 6.66675 6.66659C6.66675 4.82492 8.15841 3.33325 10.0001 3.33325C11.8417 3.33325 13.3334 4.82492 13.3334 6.66659C13.3334 8.50825 11.8417 9.99992 10.0001 9.99992ZM10.0001 11.6666C12.2251 11.6666 16.6667 12.7833 16.6667 14.9999V16.6666L3.33342 16.6666V14.9999C3.33342 12.7833 7.77508 11.6666 10.0001 11.6666Z"
                              fill="#4D4D4D"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_4020_60">
                              <rect
                                width="20"
                                height="20"
                                fill="white"
                                transform="matrix(-1 0 0 1 20 0)"
                              />
                            </clipPath>
                          </defs>
                        </svg>
                        Online 6h ago
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}
        <section>
          <PetTravelPage
            photos={profile?.data.userPhotos || []}
            cars={profile?.data.cars || []}
            reviews={profile?.data.reviews?.filter(isDisplayedDriverReview)}
            ctaUrl={ctaUrl}
            ctaLabel={ctaLabel}
          />
        </section>
      </main>
    </SWRConfig>
  )
}
